/**
 * Lightweight hooks to fetch depends_on arrays for items and item_milestones.
 * Used by the timeline connector overlay (Sub-PR 3).
 *
 * These intentionally fetch only { id, depends_on } via chunked .in queries
 * to avoid bloating existing list payloads / RPC contracts.
 */

import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { chunkedInQuerySimple } from '@/lib/queries/chunkedInQuery';

export type DependencyType = 'FS' | 'SS' | 'FF';
export type DependsOnRef = { kind: 'item' | 'milestone'; id: string; type: DependencyType; lag: number };

const normalizeDepends = (raw: any): DependsOnRef[] => {
  if (!Array.isArray(raw)) return [];
  const out: DependsOnRef[] = [];
  for (const r of raw) {
    if (!r || typeof r !== 'object') continue;
    const kind = r.kind === 'milestone' ? 'milestone' : r.kind === 'item' ? 'item' : null;
    const id = typeof r.id === 'string' ? r.id : null;
    if (!kind || !id) continue;
    const type: DependencyType = r.type === 'SS' || r.type === 'FF' ? r.type : 'FS';
    const lag = typeof r.lag === 'number' && Number.isFinite(r.lag) ? r.lag : 0;
    out.push({ kind, id, type, lag });
  }
  return out;
};

/**
 * Fetch depends_on for a list of items.
 * Returns Map<itemId, DependsOnRef[]>.
 */
export function useItemsDependsOn(itemIds: string[], options?: { enabled?: boolean }) {
  const ids = (itemIds || []).filter(Boolean);
  const key = ids.slice().sort().join(',');

  return useQuery({
    queryKey: ['items-depends-on', key],
    queryFn: async () => {
      const map = new Map<string, DependsOnRef[]>();
      if (ids.length === 0) return map;

      const rows = await chunkedInQuerySimple<{ id: string; depends_on: any }>(
        supabase as any,
        'items',
        'id',
        ids,
        'id, depends_on'
      );

      for (const r of rows || []) {
        if (!r?.id) continue;
        map.set(r.id, normalizeDepends((r as any).depends_on));
      }
      return map;
    },
    enabled: (options?.enabled !== false) && ids.length > 0,
    staleTime: 60_000,
  });
}

/**
 * Fetch depends_on for item_milestones belonging to the given item ids.
 * Returns Map<milestoneId, DependsOnRef[]>.
 */
export function useMilestonesDependsOn(itemIds: string[], options?: { enabled?: boolean }) {
  const ids = (itemIds || []).filter(Boolean);
  const key = ids.slice().sort().join(',');

  return useQuery({
    queryKey: ['milestones-depends-on', key],
    queryFn: async () => {
      const map = new Map<string, DependsOnRef[]>();
      if (ids.length === 0) return map;

      const rows = await chunkedInQuerySimple<{ id: string; depends_on: any }>(
        supabase as any,
        'item_milestones',
        'item_id',
        ids,
        'id, depends_on'
      );

      for (const r of rows || []) {
        if (!r?.id) continue;
        map.set(r.id, normalizeDepends((r as any).depends_on));
      }
      return map;
    },
    enabled: (options?.enabled !== false) && ids.length > 0,
    staleTime: 60_000,
  });
}
